import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { keyframes, styled } from '@mui/system';
import { useState, useEffect } from 'react';
import logoText from '../../assets/brand/Logo-text.svg';
import logoIcon from '../../assets/brand/logo_site.svg';

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-10px); }
`;

const glow = keyframes`
  0%, 100% { filter: drop-shadow(0 0 6px rgba(230, 29, 80, 0.0)); }
  50%       { filter: drop-shadow(0 0 18px rgba(230, 29, 80, 0.7)); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const BrandProgress = styled(LinearProgress)({
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255,0.1)',
    '& .MuiLinearProgress-bar': {
        borderRadius: 3,
        background: 'linear-gradient(90deg, #E61D50, #ff6b9d)',
    },
    '& .MuiLinearProgress-bar1Indeterminate': {
        background: 'linear-gradient(90deg, #E61D50, #ff6b9d)',
    },
    '& .MuiLinearProgress-bar2Indeterminate': {
        background: 'linear-gradient(90deg, #E61D50, #ff6b9d)',
    },
});

const Loading = ({ value = null, title = "Loading Data..." }) => {
    const [startTime] = useState(Date.now());
    const [estimatedTime, setEstimatedTime] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            const now = Date.now();
            const elapsed = Math.floor((now - startTime) / 1000);
            setElapsedTime(elapsed);

            if (value && value > 0 && value < 100 && elapsed > 2) {
                const rate = value / elapsed;
                const remaining = Math.floor((100 - value) / rate);
                setEstimatedTime(Math.max(0, remaining));
            } else if (value >= 100) {
                setEstimatedTime(0);
            } else {
                setEstimatedTime(null);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [startTime, value]);

    const formatTime = (seconds) => {
        if (seconds < 60) return `${seconds}s`;
        if (seconds < 3600) {
            const m = Math.floor(seconds / 60);
            const s = seconds % 60;
            return `${m}m ${s}s`;
        }
        return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
    };

    return (
        <Box sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(8px)',
            zIndex: 9999,
        }}>
            {/* Card */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: 380,
                maxWidth: '88vw',
                background: 'rgba(15,15,20,0.88)',
                border: '1px solid rgba(230,29,80,0.22)',
                borderRadius: '20px',
                boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)',
                p: '36px 32px 28px',
                animation: `${fadeIn} 0.4s ease both`,
            }}>
                {/* Animated icon */}
                <Box sx={{
                    mb: 2.5,
                    animation: `${float} 3s ease-in-out infinite, ${glow} 3s ease-in-out infinite`,
                    display: 'flex',
                }}>
                    <img src={logoIcon} alt="CherryClipz" style={{ height: 56, width: 'auto' }} />
                </Box>

                {/* Full logo text */}
                <Box sx={{ mb: 3 }}>
                    <img src={logoText} alt="CherryClipz" style={{ height: 24, width: 'auto', opacity: 0.9 }} />
                </Box>

                {/* Title */}
                <Typography sx={{
                    fontSize: '15px',
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.85)',
                    letterSpacing: '0.02em',
                    mb: 2.5,
                    textAlign: 'center',
                }}>
                    {title}
                </Typography>

                {/* Progress bar */}
                <Box sx={{ width: '100%', mb: 1.5 }}>
                    <BrandProgress
                        variant={value !== null ? 'determinate' : 'indeterminate'}
                        value={value || 0}
                    />
                </Box>

                {/* Percentage + time row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', px: 0.5 }}>
                    <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', fontVariantNumeric: 'tabular-nums' }}>
                        Elapsed: {formatTime(elapsedTime)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        {value !== null && (
                            <Typography sx={{ fontSize: '12px', color: '#E61D50', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                {Math.round(value)}%
                            </Typography>
                        )}
                        {/* ETA hidden near the end — the final build step breaks the
                            linear estimate and "~9s left" for minutes reads as frozen. */}
                        {estimatedTime !== null && estimatedTime > 0 && (value === null || value < 88) && (
                            <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', fontVariantNumeric: 'tabular-nums' }}>
                                ~{formatTime(estimatedTime)} left
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Honest message for the 90% wall on big transcripts */}
                {value !== null && value >= 88 && value < 100 && (
                    <Typography sx={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.55)', mt: 1.25, textAlign: 'center', lineHeight: 1.4 }}>
                        Building every row — the long part. Big transcripts can take a few
                        minutes here; it hasn't stalled.
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default Loading;
